import {
  Building2,
  Check,
  ChevronsUpDown,
  Plus,
  Search,
  Settings2,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "./cn";

export type ProjetoDaBarra = {
  readonly id: string;
  readonly nome: string;
  readonly plano?: string;
  readonly icone?: LucideIcon;
};

export type PropsDoSeletorDeProjeto = {
  readonly projetos: readonly ProjetoDaBarra[];
  readonly projetoAtivoId: string;
  readonly onMudar: (projetoId: string) => void;
  readonly soIcone?: boolean;
  readonly rotuloLista?: string;
  readonly onCriar?: () => void | undefined;
  readonly rotuloCriar?: string | undefined;
  readonly onConfigurar?: () => void | undefined;
  readonly rotuloConfigurar?: string | undefined;
  readonly onExcluir?: () => void | undefined;
  readonly rotuloExcluir?: string | undefined;
  readonly buscaHabilitada?: boolean | undefined;
};

/**
 * TeamSwitcher do sidebar-07 — seleção de workspace/cliente no topo da lateral.
 * Busca e criar workspace (ui-ux-pro-max: search as CTA + empty com ação).
 */
export function SeletorDeProjeto({
  projetos,
  projetoAtivoId,
  onMudar,
  soIcone = false,
  rotuloLista = "Workspaces",
  onCriar,
  rotuloCriar = "Novo workspace",
  onConfigurar,
  rotuloConfigurar = "Configurar workspace",
  onExcluir,
  rotuloExcluir = "Excluir projeto",
  buscaHabilitada = true,
}: PropsDoSeletorDeProjeto) {
  const [aberto, definirAberto] = useState(false);
  const [busca, definirBusca] = useState("");
  const raiz = useRef<HTMLDivElement>(null);
  const buscaRef = useRef<HTMLInputElement>(null);
  const idMenu = useId();
  const idBusca = useId();
  const ativo =
    projetos.find((p) => p.id === projetoAtivoId) ?? projetos[0] ?? null;

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return projetos;
    return projetos.filter((p) => p.nome.toLowerCase().includes(termo));
  }, [busca, projetos]);

  useEffect(() => {
    if (!aberto) {
      definirBusca("");
      return;
    }
    const fecharFora = (evento: MouseEvent) => {
      if (!raiz.current?.contains(evento.target as Node)) {
        definirAberto(false);
      }
    };
    const fecharTecla = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") definirAberto(false);
    };
    document.addEventListener("mousedown", fecharFora);
    document.addEventListener("keydown", fecharTecla);
    const t = window.setTimeout(() => buscaRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", fecharFora);
      document.removeEventListener("keydown", fecharTecla);
      window.clearTimeout(t);
    };
  }, [aberto]);

  if (!ativo && projetos.length === 0 && !onCriar) return null;

  const Icone = ativo?.icone ?? Building2;
  const nomeAtivo = ativo?.nome ?? "Workspaces";
  const temAcoes = Boolean(onCriar || onConfigurar || onExcluir);

  return (
    <div ref={raiz} className={cn("relative", soIcone ? "px-1" : "px-2")}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={aberto}
        aria-controls={idMenu}
        title={nomeAtivo}
        onClick={() => definirAberto((v) => !v)}
        className={cn(
          "flex w-full cursor-pointer items-center gap-2 rounded-lg text-left transition-colors duration-200",
          "hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
          soIcone ? "size-9 justify-center self-center p-0" : "gap-2 p-2",
          aberto && "bg-sidebar-accent text-sidebar-accent-foreground",
        )}
      >
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-sidebar-accent text-sidebar-accent-foreground">
          <Icone className="size-4" aria-hidden />
        </span>
        {!soIcone ? (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold tracking-tight">
                {nomeAtivo}
              </span>
              {ativo?.plano ? (
                <span className="block truncate text-xs text-muted-foreground">
                  {ativo.plano}
                </span>
              ) : null}
            </span>
            <ChevronsUpDown
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden
            />
          </>
        ) : null}
      </button>

      {aberto ? (
        <div
          id={idMenu}
          role="listbox"
          aria-label={rotuloLista}
          className={cn(
            "absolute z-50 mt-1 flex max-h-[min(24rem,70vh)] min-w-64 flex-col overflow-hidden rounded-xl border border-border/70 bg-popover text-popover-foreground shadow-lift",
            soIcone ? "left-0 top-full" : "inset-x-2 top-full",
          )}
        >
          <p className="px-3 pb-1 pt-2.5 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            {rotuloLista}
          </p>

          {buscaHabilitada ? (
            <div className="px-2 pb-2">
              <label htmlFor={idBusca} className="sr-only">
                Buscar workspace
              </label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <input
                  ref={buscaRef}
                  id={idBusca}
                  type="search"
                  value={busca}
                  onChange={(e) => definirBusca(e.target.value)}
                  placeholder="Buscar cliente…"
                  autoComplete="off"
                  className={cn(
                    "h-9 w-full rounded-lg border border-border/70 bg-background pl-8 pr-3 text-sm text-foreground",
                    "placeholder:text-muted-foreground",
                    "transition-colors duration-200",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  )}
                />
              </div>
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-1">
            {filtrados.length === 0 ? (
              <div className="space-y-2 px-2 py-4 text-center">
                <p className="text-sm text-muted-foreground">
                  {busca.trim()
                    ? `Nada para "${busca.trim()}". Tente outro nome.`
                    : "Nenhum workspace ainda."}
                </p>
                {onCriar ? (
                  <button
                    type="button"
                    className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-foreground transition-colors duration-150 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => {
                      definirAberto(false);
                      onCriar();
                    }}
                  >
                    <Plus className="size-3.5" aria-hidden />
                    {rotuloCriar}
                  </button>
                ) : null}
              </div>
            ) : (
              filtrados.map((projeto) => {
                const ItemIcone = projeto.icone ?? Building2;
                const selecionado = projeto.id === ativo?.id;
                return (
                  <button
                    key={projeto.id}
                    type="button"
                    role="option"
                    aria-selected={selecionado}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors duration-150",
                      "hover:bg-muted",
                      selecionado && "bg-muted",
                    )}
                    onClick={() => {
                      onMudar(projeto.id);
                      definirAberto(false);
                    }}
                  >
                    <span className="grid size-7 place-items-center rounded-md border border-border/70 bg-background">
                      <ItemIcone className="size-3.5" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {projeto.nome}
                    </span>
                    {selecionado ? (
                      <Check
                        className="size-4 shrink-0 text-foreground"
                        aria-hidden
                      />
                    ) : null}
                  </button>
                );
              })
            )}
          </div>

          {temAcoes ? (
            <div className="space-y-0.5 border-t border-border/60 p-1">
              {onConfigurar && ativo ? (
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left text-sm text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => {
                    definirAberto(false);
                    onConfigurar();
                  }}
                >
                  <Settings2 className="size-3.5" aria-hidden />
                  {rotuloConfigurar}
                </button>
              ) : null}
              {onCriar ? (
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium transition-colors duration-150 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => {
                    definirAberto(false);
                    onCriar();
                  }}
                >
                  <Plus className="size-3.5" aria-hidden />
                  {rotuloCriar}
                </button>
              ) : null}
              {onExcluir && ativo ? (
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left text-sm text-destructive transition-colors duration-150 hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => {
                    definirAberto(false);
                    onExcluir();
                  }}
                >
                  <Trash2 className="size-3.5" aria-hidden />
                  {rotuloExcluir}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export type PropsDoCabecalhoComProjeto = {
  readonly soIcone: boolean;
  readonly seletor: ReactNode;
};

/** Espaçamento do bloco superior da sidebar (seletor + hairline). */
export function CabecalhoDaLateralComProjeto({
  soIcone,
  seletor,
}: PropsDoCabecalhoComProjeto) {
  return (
    <div className="flex shrink-0 flex-col gap-2 py-3">
      {seletor}
      {!soIcone ? (
        <div
          className="mx-4 border-b border-sidebar-border"
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}
